/*
  Adjusts cart requests for the Vitals free-gift offer in Theme settings →
  Vitals gift (window.vitalsGift, set by snippets/vitals-cart-add.liquid):

  1. Gift lines Vitals adds for a tier lose their `parent_id`, so they're
     ordinary cart lines that Shopify doesn't remove with their parent.
  2. Any gift-product variant added without Vitals' gift tag (cover page,
     Quick Add, cart drawer suggestions, sticky ATC) gets one. Vitals' discount
     runs on Shopify's side and decides from the whole cart which tagged lines
     are free, so a tag never makes something free on its own.
  3. When a tier is selected and the main quantity box is above the tier's
     quantity ("Buy 4 or MORE" with 6 in the box), the box's quantity is added
     instead of the tier's — Vitals otherwise always sends the tier quantity.
  4. Vitals lowers or removes gift lines itself (a /cart/update.js keyed by
     line) after cart changes — gift lines it didn't nest under a product
     look orphaned to it. Those lowerings are dropped, so a gift stays in the
     cart (at full price once it no longer qualifies). A shopper's own change
     goes through /cart/change and is left alone, and Vitals can still raise
     a gift's quantity. When nothing is left to send, Vitals is answered
     without a cart write: with the cart Shopify returned for the change just
     before, or else with a fresh read of the cart and its sections. Shopify
     handles one cart write at a time, so an empty write would hold up the
     shopper's next change in the cart drawer.
  5. Vitals only adds gifts from the product page. When a cart drawer or cart
     page change raises the quantity of the product that earns the gift
     (Theme settings → Vitals gift), gifts are topped up to the number the
     Gift tiers give for the new total, before the cart repaints: the cover
     line already in the cart is raised, or, with none, the cover matching
     the product's options is added. A gift the shopper removed only comes
     back when they raise the quantity again.

  Only request bodies change (and step 5 adds its own cart write); requests still go through window.fetch to the
  same URL, so the cart drawer and analytics see normal cart calls.
*/
(function () {
  const config = window.vitalsGift;
  if (!config || !config.offerId || !Array.isArray(config.variants)) return;

  const giftVariantIds = new Set(config.variants.map((variant) => String(variant.id)));
  const giftTag = (variantId) => `${config.offerId}_t2g0i0v${variantId}_vdBonusItem`;
  const isGiftTag = (value) => typeof value === 'string' && value.endsWith('_vdBonusItem');
  // Line keys look like "<variant id>:<hash>".
  const isGiftKey = (key) => giftVariantIds.has(String(key).split(':')[0]);

  // Pathname of a POST request, or null.
  const postPath = (input, init) => {
    try {
      const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
      if (method !== 'POST') return null;
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
      return new URL(url, window.location.origin).pathname;
    } catch (error) {
      return null;
    }
  };

  const parseJson = (text) => {
    if (typeof text !== 'string') return null;
    try {
      const body = JSON.parse(text);
      return body && typeof body === 'object' ? body : null;
    } catch (error) {
      return null;
    }
  };

  // Quantity in the main product form's box, for the product a tier line belongs to.
  const boxQuantity = (item) => {
    const sectionId = item['section-id'];
    const input = sectionId && document.getElementById(`Quantity-${sectionId}`);
    const value = input ? parseInt(input.value, 10) : NaN;
    return Number.isInteger(value) && value > 0 ? value : null;
  };

  const adjustAddItem = (item) => {
    const properties = item.properties || {};
    if (isGiftTag(properties._oid)) {
      delete item.parent_id;
      return;
    }
    if (giftVariantIds.has(String(item.id))) {
      item.properties = { ...properties, _oid: giftTag(item.id) };
      return;
    }
    if (properties._vdTierQty) {
      const quantity = boxQuantity(item);
      if (quantity && quantity > (parseInt(item.quantity, 10) || 0)) item.quantity = quantity;
    }
  };

  // FormData (theme product forms) or URLSearchParams (Vitals' form posts).
  const tagFormGift = (data) => {
    const variantId = data.get('id');
    if (variantId && giftVariantIds.has(String(variantId)) && !data.has('properties[_oid]')) {
      data.append('properties[_oid]', giftTag(variantId));
    }
  };

  const adjustAddBody = (body) => {
    if (body instanceof FormData || body instanceof URLSearchParams) {
      tagFormGift(body);
      return body;
    }
    const json = parseJson(body);
    if (json && Array.isArray(json.items)) {
      json.items.forEach(adjustAddItem);
      return JSON.stringify(json);
    }
    if (json && json.id) {
      adjustAddItem(json);
      return JSON.stringify(json);
    }
    if (typeof body !== 'string') return body;
    const params = new URLSearchParams(body);
    if (!params.has('id')) return body;
    tagFormGift(params);
    return params.toString();
  };

  // The last full cart Shopify returned for a /cart/change or /cart/update.js,
  // with the sections it rendered. Dropped on add (its response is lines only).
  let latest = null;
  let reading = Promise.resolve();
  const remember = (response) => {
    reading = response
      .clone()
      .json()
      .then((data) => {
        if (data && Array.isArray(data.items)) latest = { data, at: Date.now() };
      })
      .catch(() => {});
    return response;
  };

  const sectionIds = (value) => (Array.isArray(value) ? value : String(value || '').split(',')).filter(Boolean);

  const jsonResponse = (data) =>
    new Response(JSON.stringify(data), { status: 200, headers: { 'Content-Type': 'application/json' } });

  // Shopify's answer to an update that changes nothing, without a cart write:
  // the latest cart we saw, or the cart and the requested sections read fresh.
  const unchangedCart = async (json) => {
    await reading;
    const ids = sectionIds(json.sections);
    if (latest && Date.now() - latest.at < 10000 && ids.every((id) => id in (latest.data.sections || {}))) {
      return jsonResponse(latest.data);
    }
    const cartUrl = window.routes?.cart_url || '/cart';
    const read = (url) => previousFetch.call(window, url).then((response) => {
      if (!response.ok) throw new Error(`${url} ${response.status}`);
      return response.json();
    });
    const sectionsUrl = new URL(cartUrl, window.location.origin);
    sectionsUrl.searchParams.set('sections', ids.join(','));
    const [cart, sections] = await Promise.all([read(`${cartUrl}.js`), ids.length ? read(sectionsUrl) : null]);
    return jsonResponse(sections ? { ...cart, sections } : cart);
  };

  // Drops entries of a keyed /cart/update.js that would lower a gift line.
  // Resolves to { body } to send, or { response } when nothing is left to send.
  const keepGiftLines = async (body) => {
    const json = parseJson(body);
    const updates = json && json.updates;
    if (!updates || Array.isArray(updates) || typeof updates !== 'object') return { body };
    const giftKeys = Object.keys(updates).filter(isGiftKey);
    if (!giftKeys.length) return { body };

    let items = null;
    if (giftKeys.some((key) => Number(updates[key]) > 0)) {
      await reading;
      items =
        latest && Date.now() - latest.at < 10000
          ? latest.data.items
          : await previousFetch
              .call(window, `${window.routes?.cart_url || '/cart'}.js`)
              .then((response) => response.json())
              .then((cart) => cart.items);
    }
    giftKeys.forEach((key) => {
      const quantity = Number(updates[key]);
      if (quantity === 0) {
        delete updates[key];
        return;
      }
      const line = items.find((item) => item.key === key);
      if (line && isGiftTag(line.properties?._oid) && quantity < line.quantity) delete updates[key];
    });

    const otherFields = Object.keys(json).filter((field) => !['updates', 'sections', 'sections_url'].includes(field));
    if (!Object.keys(updates).length && !otherFields.length) {
      return { response: await unchangedCart(json) };
    }
    return { body: JSON.stringify(json) };
  };

  // Step 5. Tiers as [quantity, gifts], lowest first: "2:1, 4:2" -> [[2, 1], [4, 2]].
  const tiers = String(config.tiers || '')
    .split(',')
    .map((tier) => tier.split(':').map((number) => parseInt(number, 10)))
    .filter(([quantity, gifts]) => quantity > 0 && gifts > 0)
    .sort((a, b) => a[0] - b[0]);

  // Gifts Vitals' discount gives for a quantity: the highest tier reached,
  // and from the top tier on, the top tier per whole block (6 -> 2, 8 -> 4).
  const earnedGifts = (quantity) => {
    const [topQuantity, topGifts] = tiers[tiers.length - 1];
    if (quantity >= topQuantity) return Math.floor(quantity / topQuantity) * topGifts;
    return tiers.filter(([tierQuantity]) => quantity >= tierQuantity).reduce((gifts, [, tierGifts]) => tierGifts, 0);
  };

  // The variant a /cart/change raises, and its quantity before: from the cart
  // the last change returned (lines can move, e.g. when a gift line is added),
  // else from the cart page's or drawer's quantity input (its value attribute
  // is the quantity Shopify last rendered).
  const raisedLine = (body) => {
    if (!config.triggerProductId || !tiers.length) return null;
    const json = parseJson(body);
    const line = json && parseInt(json.line, 10);
    if (!line) return null;
    const item = latest?.data.items[line - 1];
    const input = item ? null : document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
    const variantId = item ? String(item.variant_id) : input?.dataset.quantityVariantId;
    const before = item ? item.quantity : parseInt(input?.getAttribute('value'), 10);
    return variantId && Number(json.quantity) > before ? { variantId, request: json } : null;
  };

  // "Plush *NEW*" -> "plush"; same option matching as vitals-gift-match.js.
  const normalize = (value) =>
    String(value)
      .toLowerCase()
      .replace(/\*[^*]*\*|\([^)]*\)/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  const scoreValue = (productValue, giftValue) => {
    if (productValue === giftValue) return 3;
    const productWords = productValue.split(' ');
    const giftWords = giftValue.split(' ');
    if (productWords.every((word) => giftWords.includes(word))) return 2;
    return productWords.some((word) => giftWords.includes(word)) ? 1 : 0;
  };

  // The gift variant whose title best matches the cart line's option values.
  const matchingGift = (item) => {
    const values = (item.options_with_values || []).map((option) => normalize(option.value)).filter(Boolean);
    const score = (variant) =>
      variant.title
        .split(' / ')
        .map(normalize)
        .reduce((total, giftValue) => total + Math.max(0, ...values.map((value) => scoreValue(value, giftValue))), 0);
    const candidates = config.variants.filter((variant) => variant.available);
    return (candidates.length ? candidates : config.variants).reduce((best, variant) =>
      score(variant) > score(best) ? variant : best
    );
  };

  // After a change raised the earning product, adds the gifts it now earns.
  // Resolves to the response cart.js paints: the original one when nothing
  // was added, otherwise the cart (with the requested sections) after it.
  const topUpGifts = async (response, raised) => {
    if (!response.ok) return response;
    const cart = await response.clone().json();
    // Found by variant: lines can merge or split as discounts change.
    const raisedItem = cart.items?.find((item) => String(item.variant_id) === raised.variantId);
    if (!raisedItem || String(raisedItem.product_id) !== String(config.triggerProductId)) return response;

    const quantity = cart.items
      .filter((item) => String(item.product_id) === String(config.triggerProductId))
      .reduce((sum, item) => sum + item.quantity, 0);
    const giftLines = cart.items.filter((item) => isGiftTag(item.properties?._oid) && isGiftKey(item.key));
    const missing = earnedGifts(quantity) - giftLines.reduce((sum, item) => sum + item.quantity, 0);
    if (missing <= 0) return response;

    const post = (url, data) =>
      previousFetch.call(window, url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(data),
      });
    const { sections, sections_url } = raised.request;
    if (giftLines.length) {
      const line = giftLines[0];
      const changed = await post(window.routes?.cart_change_url || '/cart/change', {
        id: line.key,
        quantity: line.quantity + missing,
        sections,
        sections_url,
      });
      return changed.ok ? remember(changed) : response;
    }
    const gift = matchingGift(raisedItem);
    const added = await post(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
      items: [{ id: gift.id, quantity: missing, properties: { _oid: giftTag(gift.id) } }],
    });
    if (!added.ok) return response;
    latest = null;
    return unchangedCart({ sections });
  };

  const previousFetch = window.fetch;
  window.fetch = function (input, init) {
    const path = init && init.body != null ? postPath(input, init) : null;
    if (path && /\/cart\/add(\.js)?$/.test(path)) {
      latest = null;
      try {
        init = { ...init, body: adjustAddBody(init.body) };
      } catch (error) {
        console.error('vitals-cart-add:', error);
      }
    } else if (path && /\/cart\/update(\.js)?$/.test(path)) {
      return keepGiftLines(init.body)
        .catch((error) => {
          console.error('vitals-cart-add:', error);
          return { body: init.body };
        })
        .then((result) => result.response || previousFetch.call(window, input, { ...init, body: result.body }).then(remember));
    } else if (path && /\/cart\/change(\.js)?$/.test(path)) {
      const raised = raisedLine(init.body);
      const changed = previousFetch.call(window, input, init).then(remember);
      if (!raised) return changed;
      return changed.then((response) =>
        topUpGifts(response, raised).catch((error) => {
          console.error('vitals-cart-add:', error);
          return response;
        })
      );
    }
    return previousFetch.call(window, input, init);
  };
})();
